import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { CustomerCompanyAPI } from 'api/customer-company';
import { WidgetSourceType } from 'api/dashboard/widget';
import { FileGroupSourceType } from 'api/files';
import { NoteSourceType } from 'api/note';
import { ProjectAPI } from 'api/project';
import { ViewApI } from 'api/views';
import { ViewType } from 'api/views/types';
import { GENERAL_API_STALE } from 'config/query-config';
import { ACCOUNT_META_DATA_QUERY_KEY } from 'queries/account';
import { useFileGroupsQuery } from 'queries/files';
import { AllNotesBySourceKey, useGetAllCompanyNotes } from 'queries/note';
import { useRefetchProjectDetail } from 'queries/project';
import { useGetAllWidgetsBySource } from 'queries/widgets';
import { added, updated } from 'stores/companies';
export const getCustomerCompanyQueryKey = (companyId)=>[
        'companies',
        'customer',
        companyId
    ];
const getCustomerCompanyQueryKeyWithTimeRangeFinancials = (companyId, filter)=>[
        'companies',
        'customer',
        companyId,
        'financials',
        filter
    ];
export const getPeopleHierarchyQueryKey = (companyId)=>[
        'people',
        'hierarchy',
        companyId
    ];
export const getPeopleListQueryKey = (companyId)=>[
        'people',
        'list',
        companyId
    ];
const isValidCustomerCompanyId = (companyId)=>Number.isFinite(companyId) && companyId > 0;
export const useGetCustomerCompanyById = (companyId, options = {})=>{
    var _options_enabled;
    return useQuery(getCustomerCompanyQueryKey(companyId), ()=>CustomerCompanyAPI.getCompanyById(companyId), {
        refetchOnMount: true,
        staleTime: GENERAL_API_STALE,
        ...options,
        enabled: ((_options_enabled = options.enabled) !== null && _options_enabled !== void 0 ? _options_enabled : true) && isValidCustomerCompanyId(companyId)
    });
};
export const useGetCustomerCompanyByIdWithTimeRangeFinancials = (companyId, filter, options = {})=>{
    var _options_enabled;
    return useQuery(getCustomerCompanyQueryKeyWithTimeRangeFinancials(companyId, filter), ()=>CustomerCompanyAPI.getCompanyById(companyId, filter), {
        refetchOnMount: true,
        staleTime: GENERAL_API_STALE,
        ...options,
        enabled: ((_options_enabled = options.enabled) !== null && _options_enabled !== void 0 ? _options_enabled : true) && isValidCustomerCompanyId(companyId)
    });
};
export const useRefetchCustomerCompanyById = ()=>{
    const queryClient = useQueryClient();
    return useCallback(async (companyId)=>queryClient.invalidateQueries(getCustomerCompanyQueryKey(companyId)), [
        queryClient
    ]);
};
export const useGetWidgetsForCompanies = (options = {})=>useGetAllWidgetsBySource(WidgetSourceType.Company, options);
/**
 * Get valid reportees for a user (users who can report to this manager).
 * Returns full User objects directly from backend.
 */ export const useGetManagerOfOptionsQuery = (userId, options = {})=>useQuery([
        'orgChart',
        'managerOf',
        userId
    ], ()=>CustomerCompanyAPI.getValidManagerOfOptions(userId), options);
/**
 * Get valid managers for a user (users this person can report to).
 * Returns full User objects directly from backend.
 */ export const useGetReportsToOptionsQuery = (userId, options = {})=>useQuery([
        'orgChart',
        'reportsTo',
        userId
    ], ()=>CustomerCompanyAPI.getValidReportingToOptions(userId), options);
export const useGetPeopleHierarchyQuery = (companyId, options = {})=>useQuery([
        'people',
        'hierarchy',
        companyId
    ], ()=>CustomerCompanyAPI.getPeopleHierarchy(companyId), options);
export const useGetAllNotesByCompanyId = (companyId, options = {})=>useGetAllCompanyNotes({
        sourceId: companyId,
        sourceType: NoteSourceType.Company,
        subSourceTypes: [
            NoteSourceType.Project
        ]
    }, options);
export const useRefetchAllNotesByCompanyId = ()=>{
    const queryClient = useQueryClient();
    const refetchCustomerCompany = useRefetchCustomerCompanyById();
    return useCallback(async (companyId)=>{
        await queryClient.invalidateQueries(AllNotesBySourceKey({
            sourceId: companyId,
            sourceType: NoteSourceType.Company
        }));
        await refetchCustomerCompany(companyId);
    }, [
        queryClient,
        refetchCustomerCompany
    ]);
};
export const useRefetchAllNotes = ()=>{
    const queryClient = useQueryClient();
    const refetchCustomerCompany = useRefetchCustomerCompanyById();
    const refetchProjectDetail = useRefetchProjectDetail();
    return useCallback(async (sourceId, sourceType)=>{
        await queryClient.invalidateQueries(AllNotesBySourceKey({
            sourceId,
            sourceType
        }));
        if (sourceType === NoteSourceType.Company) {
            await refetchCustomerCompany(sourceId);
        } else {
            await refetchProjectDetail(sourceId);
        }
    }, [
        queryClient,
        refetchCustomerCompany,
        refetchProjectDetail
    ]);
};
export const useGetAllFileGroupsByCompanyId = (companyId)=>useFileGroupsQuery({
        sourceId: companyId,
        sourceType: FileGroupSourceType.Company
    });
export const useRefetchAllFileGroupsByCompanyId = (companyId)=>{
    const { refetch } = useFileGroupsQuery({
        sourceId: companyId,
        sourceType: FileGroupSourceType.Company
    });
    return useCallback(async ()=>{
        await refetch();
    }, [
        refetch
    ]);
};
export const useCompanyPeopleListQuery = (companyId, options = {})=>{
    return useQuery(getPeopleListQueryKey(companyId), ()=>CustomerCompanyAPI.getCompanyPeopleList(companyId), options);
};
export const getStatusUpdatesByCompanyKey = (companyId)=>[
        'status-updates',
        'by-company-id',
        companyId
    ];
export const useGetStatusUpdatesByCompany = (companyId, options = {})=>useQuery(getStatusUpdatesByCompanyKey(companyId), ()=>CustomerCompanyAPI.getStatusUpdatesByCompanyId(companyId), options);
export const useRefetchStatusUpdateByCompany = ()=>{
    const queryClient = useQueryClient();
    return useCallback(async (companyId)=>{
        queryClient.invalidateQueries(getStatusUpdatesByCompanyKey(companyId));
    }, [
        queryClient
    ]);
};
export const CustomerCompanyListViews = [
    'customer-company',
    'views'
];
export const useGetCustomerCompanyListViewsQuery = (queryOptions = {})=>useQuery(CustomerCompanyListViews, ()=>ViewApI.getAllViews(ViewType.CustomerPorfolioList), queryOptions);
export const getProjectsByCompanyIdKey = (companyId)=>[
        'projects-by-customer',
        companyId
    ];
export const useGetProjectsByCompany = (companyId, options = {})=>useQuery(getProjectsByCompanyIdKey(companyId), ()=>ProjectAPI.getProjectsByCustomer(companyId), options);
export const useCreateCompanyMutation = (options = {})=>{
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    return useMutation((payload)=>CustomerCompanyAPI.createCompany(payload), {
        onSuccess: async (company)=>{
            dispatch(added(company));
            await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
        },
        ...options
    });
};
export const useUpdateCustomerCompanyMutation = (options = {})=>{
    const dispatch = useDispatch();
    const refetchCustomerCompany = useRefetchCustomerCompanyById();
    return useMutation(({ companyId, payload })=>CustomerCompanyAPI.updateCompany(companyId, payload), {
        onSuccess: async (company)=>{
            dispatch(updated(company));
            await refetchCustomerCompany(company.companyId);
        },
        ...options
    });
};
export const useDeleteCustomerCompanyMutation = (onSuccess)=>{
    return useMutation((accountId)=>CustomerCompanyAPI.deleteCustomerCompany(accountId), {
        onSuccess: async ()=>{
            if (onSuccess) {
                onSuccess();
            }
        }
    });
};
export const useMergeCompanyMutation = (options = {})=>{
    return useMutation(({ destinationCompanyId, sourceCompanyIds })=>CustomerCompanyAPI.mergeCompanies(sourceCompanyIds, destinationCompanyId), options);
};
export const useGetAllCustomerCompanies = (options = {})=>{
    return useQuery([
        'customer-companies'
    ], ()=>CustomerCompanyAPI.getAllCustomerCompanies(), options);
};
export const useGetCustomerCompanyExport = ()=>{
    return useMutation((data)=>CustomerCompanyAPI.exportCustomerCompanies(data));
};
